// ZoomableImage.jsx
// In-app pinch-to-zoom + pan wrapper for a single image. Exists because the
// browser's default viewport pinch zoom conflicts with the gallery detail's
// edge-tap prev/next handles — two-finger gestures dislodge the layout and
// often trigger stray navigation.
//
// Gesture model:
//   - Two-finger pinch → scale (clamped 1.0 – 4.0)
//   - Two-finger drag while pinching → translate
//   - One-finger drag while zoomed → translate
//   - Double-tap → toggle between 1× and 2.5× at the tap point
//   - `touch-action: none` on the wrapper blocks the browser's own gestures
//
// Props:
//   src            (string) image URL
//   alt            (string)
//   imgStyle       (object) styling applied to the <img> itself (objectFit etc.)
//   wrapperStyle   (object) styling for the outer framed box
//   filter         (string) CSS filter (drop-shadow, etc.) applied to <img>
//   onZoomChange   (fn)     fires with `true` when scale > 1, `false` otherwise —
//                           parent uses this to hide edge-tap nav while zoomed.

import { useEffect, useRef, useState } from "react";

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_MS = 300;

export default function ZoomableImage({
  src,
  alt,
  imgStyle,
  wrapperStyle,
  filter,
  onZoomChange,
}) {
  const containerRef = useRef(null);
  const gestureRef   = useRef(null);    // active gesture state
  const lastTapRef   = useRef(0);       // timestamp of last tap (double-tap detection)
  const [scale,  setScale]  = useState(1);
  const [tx, setTx] = useState(0);      // translation x, in px
  const [ty, setTy] = useState(0);

  // Notify parent when we cross the zoomed threshold so it can hide nav.
  const zoomedRef = useRef(false);
  useEffect(() => {
    const zoomed = scale > 1.01;
    if (zoomed !== zoomedRef.current) {
      zoomedRef.current = zoomed;
      onZoomChange?.(zoomed);
    }
  }, [scale, onZoomChange]);

  // Reset transform when the image source changes — otherwise you'd swipe
  // through gallery items at whatever zoom the previous one ended on.
  useEffect(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, [src]);

  const reset = () => {
    setScale(1);
    setTx(0);
    setTy(0);
  };

  // Clamp the pan so the image can't be flung completely off-frame. The
  // container acts as the viewport; max pan in each axis is (scale - 1) / 2
  // times the container's matching dimension.
  const clampPan = (nx, ny, s) => {
    const el = containerRef.current;
    if (!el) return { tx: nx, ty: ny };
    const w = el.clientWidth, h = el.clientHeight;
    const maxX = (s - 1) * w / 2;
    const maxY = (s - 1) * h / 2;
    return {
      tx: Math.max(-maxX, Math.min(maxX, nx)),
      ty: Math.max(-maxY, Math.min(maxY, ny)),
    };
  };

  const distBetween = (t1, t2) => {
    const dx = t2.clientX - t1.clientX;
    const dy = t2.clientY - t1.clientY;
    return Math.hypot(dx, dy);
  };
  const midBetween = (t1, t2) => ({
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  });

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      gestureRef.current = {
        kind:        "pinch",
        startDist:   distBetween(e.touches[0], e.touches[1]),
        startMid:    midBetween(e.touches[0], e.touches[1]),
        startScale:  scale,
        startTx:     tx,
        startTy:     ty,
      };
    } else if (e.touches.length === 1 && scale > 1) {
      gestureRef.current = {
        kind:       "pan",
        startX:     e.touches[0].clientX,
        startY:     e.touches[0].clientY,
        startTx:    tx,
        startTy:    ty,
      };
    } else if (e.touches.length === 1) {
      // Double-tap detection on untouched (scale=1) image.
      const now = Date.now();
      if (now - lastTapRef.current < DOUBLE_TAP_MS) {
        e.preventDefault();
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          // Zoom toward the tap point: shift translation so the tapped pixel
          // stays under the finger after scaling to 2.5×.
          const tapX = e.touches[0].clientX - rect.left - rect.width  / 2;
          const tapY = e.touches[0].clientY - rect.top  - rect.height / 2;
          const newScale = 2.5;
          const delta = newScale - 1;
          const clamped = clampPan(-tapX * delta, -tapY * delta, newScale);
          setScale(newScale);
          setTx(clamped.tx);
          setTy(clamped.ty);
        }
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
    }
  };

  const onTouchMove = (e) => {
    const g = gestureRef.current;
    if (!g) return;
    if (g.kind === "pinch" && e.touches.length === 2) {
      e.preventDefault();
      const dist = distBetween(e.touches[0], e.touches[1]);
      const mid  = midBetween(e.touches[0], e.touches[1]);
      const ratio = dist / Math.max(1, g.startDist);
      const nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, g.startScale * ratio));
      const panDx = mid.x - g.startMid.x;
      const panDy = mid.y - g.startMid.y;
      const clamped = clampPan(g.startTx + panDx, g.startTy + panDy, nextScale);
      setScale(nextScale);
      setTx(clamped.tx);
      setTy(clamped.ty);
    } else if (g.kind === "pan" && e.touches.length === 1) {
      e.preventDefault();
      const dx = e.touches[0].clientX - g.startX;
      const dy = e.touches[0].clientY - g.startY;
      const clamped = clampPan(g.startTx + dx, g.startTy + dy, scale);
      setTx(clamped.tx);
      setTy(clamped.ty);
    }
  };

  const onTouchEnd = (e) => {
    // Snap back to 1× when the user fully pinches in beyond ~0.95. Makes the
    // gesture feel sticky at rest instead of leaving the image at 0.99×.
    if (scale < 1.02) {
      setScale(1);
      setTx(0);
      setTy(0);
    }
    if (e.touches.length < 2) {
      gestureRef.current = null;
    }
  };

  // Desktop wheel zoom — ctrl/meta + wheel, matching common image-viewer UX.
  const onWheel = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    const delta = -e.deltaY * 0.002;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale + delta));
    const tapX = e.clientX - rect.left - rect.width / 2;
    const tapY = e.clientY - rect.top  - rect.height / 2;
    const deltaRatio = (nextScale - scale) / Math.max(0.001, scale);
    const clamped = clampPan(tx - tapX * deltaRatio, ty - tapY * deltaRatio, nextScale);
    setScale(nextScale);
    setTx(clamped.tx);
    setTy(clamped.ty);
  };

  const zoomed = scale > 1.01;

  return (
    <div
      ref={containerRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      onWheel={onWheel}
      style={{
        ...wrapperStyle,
        position: "relative",
        touchAction: "none",  // block browser viewport zoom + scroll-cancel our pan
        overflow: "hidden",
        WebkitUserSelect: "none",
        userSelect: "none",
      }}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        style={{
          ...imgStyle,
          transform:         `translate(${tx.toFixed(1)}px, ${ty.toFixed(1)}px) scale(${scale.toFixed(3)})`,
          transformOrigin:   "center center",
          transition:        gestureRef.current ? "none" : "transform 0.18s ease-out",
          filter,
          pointerEvents:     "none",
        }}
      />
      {zoomed && (
        <button
          type="button"
          onClick={reset}
          style={{
            position:  "absolute",
            bottom:    "0.75rem",
            right:     "0.75rem",
            background: "rgba(13,24,51,0.85)",
            color:      "#ffdaad",
            border:     "1px solid rgba(252,179,31,0.55)",
            borderRadius: "999px",
            padding:   "0.4rem 0.85rem",
            fontSize:  "0.78rem",
            fontWeight: 700,
            cursor:     "pointer",
            backdropFilter: "blur(6px)",
            zIndex:     5,
          }}
        >
          🔄 Reset zoom
        </button>
      )}
    </div>
  );
}
